import { Filter } from 'components/molecules/filters/types';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover-v2';
import {
  ApiFilter,
  ApiGroupFilter,
} from 'utils/simple-search-filter/simple-search-filter';

/**
 * Checks if a filter or any of its nested filters have valid criteria
 */
export const hasValidFilters = (filter: Filter): boolean => {
  if (!filter) {
    return false;
  }
  const hasValidCriteria =
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    filter?.criteria?.filter?.((x) => FieldFilter.isValid(x))?.length > 0;

  if (hasValidCriteria) {
    return true;
  }
  if (filter.nestedFilter?.length) {
    return filter.nestedFilter.some(hasValidFilters);
  }

  return false;
};

/**
 * Checks if a grouped filter or any of its nested filters have valid criteria
 */
export const hasValidGroupFilters = (filter: GroupedFilter): boolean => {
  if (!filter) {
    return false;
  }
  const hasValidCriteria =
    filter.groupCriteria?.filter((group) => FieldFilter.isValid(group.criteria))
      .length > 0;

  if (hasValidCriteria) {
    return true;
  }
  if (filter.nestedFilters?.length) {
    return filter.nestedFilters.some(hasValidGroupFilters);
  }

  return false;
};

/**
 * Extracts all valid filters from a filter and its nested filters
 */
export const extractAllValidFilters = (filter: Filter): FieldFilter[] => {
  if (!filter) {
    return [];
  }
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  const currentFilters = (filter.criteria ?? []).filter(FieldFilter.isValid);

  if (filter.nestedFilter?.length) {
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    return [
      ...currentFilters,
      ...filter.nestedFilter.flatMap(extractAllValidFilters),
    ];
  }

  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  return currentFilters;
};

/**
 * Compares two filters hierarchically, level by level
 */
export const compareFiltersHierarchically = (
  filter1: Filter,
  filter2: Filter,
): boolean => {
  if (!filter1 && !filter2) {
    return true;
  }
  if (!filter1 || !filter2) {
    return false;
  }

  if (filter1.type !== filter2.type) {
    return false;
  }

  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  const validCriteria1 = (filter1.criteria ?? []).filter(FieldFilter.isValid);
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  const validCriteria2 = (filter2.criteria ?? []).filter(FieldFilter.isValid);

  if (validCriteria1.length !== validCriteria2.length) {
    return false;
  }

  const sortedCriteria1 = validCriteria1.sort((a, b) =>
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    a.field.fieldName.localeCompare(b.field.fieldName),
  );
  const sortedCriteria2 = validCriteria2.sort((a, b) =>
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    a.field.fieldName.localeCompare(b.field.fieldName),
  );

  if (
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    ApiFilter.toQueryStringFormat(sortedCriteria1) !==
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    ApiFilter.toQueryStringFormat(sortedCriteria2)
  ) {
    return false;
  }

  const nestedFilters1 = filter1.nestedFilter ?? [];
  const nestedFilters2 = filter2.nestedFilter ?? [];

  if (nestedFilters1.length !== nestedFilters2.length) {
    return false;
  }

  const sortNestedFilters = (filters: Filter[]) => {
    return filters.sort((a, b) => {
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      const criteriaA = (a.criteria ?? []).filter(FieldFilter.isValid);
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      const criteriaB = (b.criteria ?? []).filter(FieldFilter.isValid);

      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      return ApiFilter.toQueryStringFormat(criteriaA).localeCompare(
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        ApiFilter.toQueryStringFormat(criteriaB),
      );
    });
  };

  const sortedNested1 = sortNestedFilters(
    nestedFilters1.map((filter) => ({ ...filter })),
  );
  const sortedNested2 = sortNestedFilters(
    nestedFilters2.map((filter) => ({ ...filter })),
  );

  return sortedNested1.every((nestedFilter1, index) =>
    compareFiltersHierarchically(nestedFilter1, sortedNested2[index]),
  );
};

/**
 * Compares two group filters hierarchically, level by level
 * This function is specifically designed to handle group-based filters
 */
export const compareGroupFiltersHierarchically = (
  filter1: GroupedFilter,
  filter2: GroupedFilter,
): boolean => {
  if (!filter1 && !filter2) {
    return true;
  }
  if (!filter1 || !filter2) {
    return false;
  }

  if (filter1.type !== filter2.type) {
    return false;
  }

  const validCriteria1 = (filter1.groupCriteria ?? []).filter((filter) =>
    FieldFilter.isValid(filter.criteria),
  );
  const validCriteria2 = (filter2.groupCriteria ?? []).filter((filter) =>
    FieldFilter.isValid(filter.criteria),
  );

  if (validCriteria1.length !== validCriteria2.length) {
    return false;
  }

  const sortedCriteria1 = validCriteria1.sort((a, b) =>
    a.criteria.field.fieldName.localeCompare(b.criteria.field.fieldName),
  );
  const sortedCriteria2 = validCriteria2.sort((a, b) =>
    a.criteria.field.fieldName.localeCompare(b.criteria.field.fieldName),
  );

  if (
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    ApiGroupFilter.toQueryStringFormatForGroups(sortedCriteria1) !==
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    ApiGroupFilter.toQueryStringFormatForGroups(sortedCriteria2)
  ) {
    return false;
  }

  const nestedGroups1 = filter1.nestedFilters ?? [];
  const nestedGroups2 = filter2.nestedFilters ?? [];

  if (nestedGroups1.length !== nestedGroups2.length) {
    return false;
  }

  const sortNestedGroups = (groups: GroupedFilter[]) => {
    return groups.sort((a, b) => {
      const criteriaA = (a.groupCriteria ?? []).filter((group) =>
        FieldFilter.isValid(group.criteria),
      );
      const criteriaB = (b.groupCriteria ?? []).filter((group) =>
        FieldFilter.isValid(group.criteria),
      );

      return ApiGroupFilter.toQueryStringFormatForGroups(
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        criteriaA,
      ).localeCompare(
        ApiGroupFilter.toQueryStringFormatForGroups(criteriaB as any),
      );
    });
  };

  // Clone arrays before sorting to prevent mutating the original arrays
  const sortedNested1 = sortNestedGroups(
    nestedGroups1.map((group) => ({ ...group })),
  );
  const sortedNested2 = sortNestedGroups(
    nestedGroups2.map((group) => ({ ...group })),
  );

  return sortedNested1.every((nestedGroup1, index) =>
    compareGroupFiltersHierarchically(nestedGroup1, sortedNested2[index]),
  );
};
